import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../auth';
import { useApp } from '../context/AppContext';
import { useAPIStorage } from '../hooks/useAPIStorage';
import { DEFAULT_SETTINGS } from '../types';
import { getHolidaysForState, type FederalState } from '../data/germanHolidays';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import type { DayEntry, CompanyVacationRule } from '../types';
import { AUTH_SESSION_EXPIRED_EVENT } from '../auth/authEvents';
import { getCompanyVacationDates, mergeCompanyVacationIntoEntries } from '../utils/companyVacation';
import type { WageType } from '../types';

/** Leitet mfaVerified aus entraMfaVerified und mfaEnabled ab */
function deriveMfaVerified(entraMfaVerified: boolean, mfaEnabled: boolean): boolean {
  return entraMfaVerified || !mfaEnabled;
}

interface Props {
  children: React.ReactNode;
}

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 3000, 9000];

/**
 * Fügt Feiertage zu den Entries hinzu (für korrekte lastSaved-Berechnung)
 */
function addHolidaysToEntries(
  entries: Record<string, DayEntry>,
  year: number,
  federalState: FederalState
): Record<string, DayEntry> {
  const holidays = getHolidaysForState(year, federalState);
  const result = { ...entries };

  holidays.forEach((holiday) => {
    const existingEntry = result[holiday.date];
    if (!existingEntry) {
      result[holiday.date] = {
        date: holiday.date,
        hours: 0,
        isHoliday: true,
        isVacation: false,
        note: holiday.name,
      };
    } else if (!existingEntry.isHoliday) {
      result[holiday.date] = {
        ...existingEntry,
        isHoliday: true,
        note: existingEntry.note || holiday.name,
      };
    }
  });

  return result;
}

/**
 * Lädt Daten von der API beim App-Start und synchronisiert Änderungen
 * Ersetzt SharePointDataLoader für PostgreSQL-Backend
 */
export function APIDataLoader({ children }: Props) {
  const { isAuthenticated, user, authMode, getAccessToken, updateManagerStatus, updateMfaState } = useAuth();
  const { state, importData, setSettingsSegments, setUserWageType, setCarryOver } = useApp();
  const {
    login,
    loginSso,
    getMe,
    getSettings,
    getEntries,
    getCarryOver,
    saveSettings,
    saveEntries,
    saveCarryOver,
    getCompanyVacationRule,
    getMySettingsSegments,
    storage,
  } = useAPIStorage();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'pending'>('idle');
  const [saveError, setSaveError] = useState<string | undefined>();

  // Refs um zu tracken was bereits geladen/gespeichert wurde
  const hasLoadedFromAPI = useRef(false);
  const hasLoggedIn = useRef(false);
  const loginAttemptRef = useRef(false);
  const lastSavedSettingsRef = useRef<string>('');
  const lastSavedEntriesRef = useRef<string>('');

  const companyVacationRuleRef = useRef<CompanyVacationRule | null>(null);
  const userWageTypeRef = useRef<WageType | null>(null);

  // New save mechanism refs
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<{ year: number; settings: string; entries: string } | null>(null);
  const retryCountRef = useRef(0);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // stateRef always has the latest state values (for beforeunload)
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Bei der API anmelden wenn authentifiziert
  useEffect(() => {
    const doLogin = async () => {
      if (!isAuthenticated || !user || hasLoggedIn.current) {
        return;
      }

      // Loop guard: prevent automatic re-login after a failed attempt.
      // Reset only on remount (e.g. after explicit re-login via LoginPage).
      if (loginAttemptRef.current) {
        return;
      }

      // Prüfe ob bereits ein gültiges JWT im Storage liegt → Login überspringen
      if (storage.hasValidToken()) {
        console.log('APIDataLoader: Gültiges JWT vorhanden, überspringe Login');

        // MFA-Status aus JWT-Payload wiederherstellen (sonst gehen entraMfaVerified + Grace-Info verloren)
        const payload = storage.getTokenPayload();
        if (payload) {
          const entraMfaVerified = payload.entraMfaVerified === true;
          const mfaEnabled = payload.mfaEnabled === true;

          // Prüfe ob das JWT das entraMfaVerified-Feld überhaupt enthält
          // (alte JWTs von vor dem Entra-MFA-Fix haben es nicht)
          const hasEntraMfaField = 'entraMfaVerified' in payload;

          if (!hasEntraMfaField) {
            // Altes JWT ohne entraMfaVerified-Feld — frisches Login erzwingen
            console.log('APIDataLoader: Altes JWT ohne entraMfaVerified-Feld, erzwinge frisches Login');
            try {
              const accessToken = await getAccessToken();
              const loginResult = authMode === 'teams'
                ? await loginSso(accessToken)
                : await login(accessToken);
              hasLoggedIn.current = true;
              setIsLoggedIn(true);
              if (loginResult?.user?.isManager !== undefined) {
                updateManagerStatus(loginResult.user.isManager);
              }
              const freshEntraMfa = loginResult?.entraMfaVerified ?? false;
              if (loginResult?.mfaRequired !== undefined) {
                updateMfaState({
                  mfaEnabled: loginResult.mfaRequired,
                  mfaVerified: freshEntraMfa || !loginResult.mfaRequired,
                  entraMfaVerified: freshEntraMfa,
                });
              } else {
                updateMfaState({ entraMfaVerified: freshEntraMfa });
              }
              return; // JWT wurde aktualisiert, Rest des JWT-Reuse-Pfads überspringen
            } catch (e) {
              console.warn('APIDataLoader: Frisches Login fehlgeschlagen, nutze bestehendes JWT', e);
              // Fallthrough — weiter mit bestehendem JWT (konservativ: entraMfaVerified = false)
            }
          }

          hasLoggedIn.current = true;
          setIsLoggedIn(true);
          updateMfaState({
            mfaEnabled,
            mfaVerified: entraMfaVerified || (payload.mfaVerified === true),
            entraMfaVerified,
          });
          if (typeof payload.isManager === 'boolean') {
            updateManagerStatus(payload.isManager);
          }
          // Grace-Period-Info vom Backend laden (nur wenn Entra-MFA nicht aktiv)
          if (!entraMfaVerified && !mfaEnabled) {
            storage.getMfaStatus().then((mfaStatus) => {
              updateMfaState({
                mfaEnabled: mfaStatus.mfaEnabled,
                mfaVerified: deriveMfaVerified(mfaStatus.entraMfaVerified ?? false, mfaStatus.mfaEnabled),
                entraMfaVerified: mfaStatus.entraMfaVerified ?? false,
                graceDaysRemaining: mfaStatus.graceDaysRemaining,
                graceExpired: mfaStatus.graceExpired,
              });
            }).catch(() => { /* MFA-Status-Abruf fehlgeschlagen — kein Blocker */ });
          }
        } else {
          hasLoggedIn.current = true;
          setIsLoggedIn(true);
        }

        return;
      }

      // Clear expired JWT so it's not sent in the Authorization header during login
      storage.clearToken();
      loginAttemptRef.current = true;

      try {
        console.log('APIDataLoader: Hole Access Token...');
        let accessToken = await getAccessToken();
        console.log('APIDataLoader: Melde bei API an...');

        // Try login — if the cached MSAL token is server-side invalid (401),
        // retry exactly once with a force-refreshed token from Microsoft.
        // Im Teams-Mode liefert getAccessToken einen SSO-Token → /auth/login-sso.
        let loginResult;
        try {
          loginResult = authMode === 'teams'
            ? await loginSso(accessToken)
            : await login(accessToken);
        } catch (loginError) {
          console.warn('APIDataLoader: Login fehlgeschlagen, versuche Token-Refresh...', loginError);
          accessToken = await getAccessToken(true);
          loginResult = authMode === 'teams'
            ? await loginSso(accessToken)
            : await login(accessToken);
        }

        hasLoggedIn.current = true;
        setIsLoggedIn(true);
        // Manager-Status aus Backend-JWT aktualisieren
        if (loginResult?.user?.isManager !== undefined) {
          updateManagerStatus(loginResult.user.isManager);
        }
        // Entra-MFA-Status aus Login-Response übernehmen
        const entraMfaVerified = loginResult?.entraMfaVerified ?? false;
        // MFA-Status aus Login-Response aktualisieren
        if (loginResult?.mfaRequired !== undefined) {
          updateMfaState({
            mfaEnabled: loginResult.mfaRequired,
            mfaVerified: entraMfaVerified || !loginResult.mfaRequired,
            entraMfaVerified,
          });
        } else {
          updateMfaState({ entraMfaVerified });
        }
        console.log('APIDataLoader: API Login erfolgreich');
      } catch (e) {
        console.error('APIDataLoader: API Login fehlgeschlagen:', e);
        // Show login page — session-expired may already have been dispatched
        // by getAccessToken for InteractionRequired/BrowserAuth errors
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
        setError('Anmeldung bei der API fehlgeschlagen');
        setIsLoading(false);
      }
    };

    void doLogin();
  }, [isAuthenticated, user, getAccessToken, login, loginSso, storage, updateManagerStatus, updateMfaState, authMode]);

  /**
   * Core save execution with queuing and retry logic.
   * @param forceRetry - if true, resets retry count and retries immediately (for manual retry button)
   */
  const executeSave = useCallback(async (forceRetry = false) => {
    if (!isAuthenticated || !hasLoadedFromAPI.current || !hasLoggedIn.current) {
      return;
    }

    const currentSettings = JSON.stringify(stateRef.current.settings);
    const currentEntries = JSON.stringify(stateRef.current.entries);
    const currentYear = stateRef.current.year;

    const settingsChanged = currentSettings !== lastSavedSettingsRef.current;
    const entriesChanged = currentEntries !== lastSavedEntriesRef.current;

    if (!settingsChanged && !entriesChanged) {
      return;
    }

    // SAFETY: never push empty entries to the backend — empty state means "not loaded", not "user deleted everything"
    if (entriesChanged) {
      const currentEntriesCount = Object.keys(stateRef.current.entries).length;
      if (currentEntriesCount === 0) {
        console.error('APIDataLoader: SAFETY STOP — refusing to sync empty entries to backend. Entries not loaded or localStorage was cleared.');
        return;
      }

      // SAFETY: detect holidays-only state (all entries have hours=0 and isHoliday=true) — this is a freshly initialized state, not real user data
      const isNonHoliday = (e: DayEntry) => e.hours > 0 || e.isVacation || e.isSickDay;
      const entriesValues = Object.values(stateRef.current.entries);
      const currentNonHolidayCount = entriesValues.filter(isNonHoliday).length;
      const allHolidaysOnly = currentNonHolidayCount === 0 && entriesValues.length > 0;

      if (allHolidaysOnly) {
        let previousNonHolidayCount = 0;
        try {
          if (lastSavedEntriesRef.current) {
            const prev = JSON.parse(lastSavedEntriesRef.current) || {};
            previousNonHolidayCount = (Object.values(prev) as DayEntry[]).filter(
              (e) => e.hours > 0 || e.isVacation || e.isSickDay
            ).length;
          }
        } catch {
          previousNonHolidayCount = 0;
        }
        if (previousNonHolidayCount > 0) {
          console.error('APIDataLoader: SAFETY STOP — entries contain only holidays but backend had', previousNonHolidayCount, 'non-holiday entries. Likely a freshly initialized state after localStorage.clear().');
          return;
        }
      }
    }

    // If already saving: queue the latest data instead of dropping it
    if (isSavingRef.current && !forceRetry) {
      console.log('APIDataLoader: Save in progress, queuing pending save for year', currentYear);
      pendingSaveRef.current = { year: currentYear, settings: currentSettings, entries: currentEntries };
      setSaveStatus('pending');
      return;
    }

    if (forceRetry) {
      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }

    isSavingRef.current = true;
    setSaveStatus('saving');
    setSaveError(undefined);

    try {
      if (settingsChanged) {
        console.log('APIDataLoader: Speichere Einstellungen');
        await saveSettings(stateRef.current.settings);
        lastSavedSettingsRef.current = currentSettings;
        console.log('APIDataLoader: Einstellungen gespeichert');
      }

      if (entriesChanged) {
        console.log('APIDataLoader: Speichere Einträge', {
          year: currentYear,
          entriesCount: Object.keys(stateRef.current.entries).length
        });
        await saveEntries(currentYear, stateRef.current.entries);
        lastSavedEntriesRef.current = currentEntries;
        console.log('APIDataLoader: Einträge gespeichert für Jahr', currentYear);
      }

      console.log('APIDataLoader: Speichern erfolgreich');
      retryCountRef.current = 0;
      setSaveStatus('saved');
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Unbekannter Fehler beim Speichern';
      console.error('APIDataLoader: Fehler beim Speichern:', e);

      // Urlaubslimit überschritten: permanenter Fehler, kein Retry
      if (e instanceof Error && 'code' in e && e.code === 'VACATION_LIMIT_EXCEEDED') {
        setSaveStatus('error');
        setSaveError(errorMsg);
        isSavingRef.current = false;
        return;
      }

      retryCountRef.current += 1;
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCountRef.current - 1] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
        console.log(`APIDataLoader: Retry ${retryCountRef.current}/${MAX_RETRIES} in ${delay}ms`);
        setSaveStatus('error');
        setSaveError(`${errorMsg} (Versuch ${retryCountRef.current}/${MAX_RETRIES})`);

        isSavingRef.current = false;
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          void executeSave();
        }, delay);
        return;
      } else {
        console.error(`APIDataLoader: Max retries (${MAX_RETRIES}) exceeded. Manual retry needed.`);
        setSaveStatus('error');
        setSaveError(`${errorMsg} - Automatische Wiederholung fehlgeschlagen`);
      }
    } finally {
      isSavingRef.current = false;
    }

    // Process pending save if one was queued
    const pending = pendingSaveRef.current;
    if (pending) {
      console.log('APIDataLoader: Processing queued save for year', pending.year);
      pendingSaveRef.current = null;
      // The pending data is already reflected in stateRef.current, so just call executeSave again
      void executeSave();
    }
  }, [isAuthenticated, saveSettings, saveEntries]);

  /**
   * Flush any pending/debounced save synchronously for the current year.
   * Used before year changes to ensure old year data is saved.
   */
  const flushSave = useCallback(async () => {
    // Cancel any pending debounce
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    // Cancel any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Wait for any in-progress save to complete
    const waitForSave = () => new Promise<void>((resolve) => {
      const check = () => {
        if (!isSavingRef.current) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });

    await waitForSave();

    // Now save if there are unsaved changes
    const currentSettings = JSON.stringify(stateRef.current.settings);
    const currentEntries = JSON.stringify(stateRef.current.entries);

    if (currentSettings !== lastSavedSettingsRef.current || currentEntries !== lastSavedEntriesRef.current) {
      console.log('APIDataLoader: Flushing unsaved changes before year change');
      retryCountRef.current = 0;
      await executeSave();
    }
  }, [executeSave]);

  // Beim Start: Daten von API laden
  useEffect(() => {
    const loadFromAPI = async () => {
      if (!isAuthenticated || !user || !isLoggedIn) {
        return;
      }

      // Verhindere erneutes Laden
      if (hasLoadedFromAPI.current) {
        setIsLoading(false);
        return;
      }

      console.log('APIDataLoader: Lade Daten für', user.name);
      setIsLoading(true);
      setError(null);

      try {
        const currentYear = state.year;

        // Parallel laden
        const [settings, entries, carryOver, vacationRule, settingsSegments, me] = await Promise.all([
          getSettings(),
          getEntries(currentYear),
          getCarryOver(currentYear),
          getCompanyVacationRule(),
          getMySettingsSegments(currentYear),
          getMe(),
        ]);

        console.log('APIDataLoader: Daten geladen', {
          hasSettings: !!settings,
          entriesCount: entries ? Object.keys(entries).length : 0,
          hasCarryOver: !!carryOver,
          hasVacationRule: !!vacationRule,
          settingsSegments: settingsSegments.length,
        });

        // Betriebsferien-Regel und WageType merken für Jahreswechsel
        companyVacationRuleRef.current = vacationRule;
        userWageTypeRef.current = me?.wageType ?? null;
        setUserWageType(me?.wageType ?? null);

        // Daten in AppContext setzen
        const finalSettings = settings || DEFAULT_SETTINGS;
        let mergedEntries: Record<string, DayEntry> = entries || {};

        // Betriebsferien VOR dem Dispatch in die Entries mergen,
        // um Stale-Closure-Bug bei applyCompanyVacation zu vermeiden.
        // WageType-Differenzierung: FIXED = erzwungener Urlaub, HOURLY/null = gesperrter Tag ohne Urlaubsabzug.
        if (vacationRule) {
          const vacationDates = getCompanyVacationDates(vacationRule, currentYear);
          mergedEntries = mergeCompanyVacationIntoEntries(mergedEntries, vacationDates, userWageTypeRef.current);
        }

        // Feiertage hinzufügen BEVOR wir lastSaved setzen
        const finalEntries = addHolidaysToEntries(mergedEntries, currentYear, finalSettings.federalState);

        // Speichere die aktuellen Werte als "zuletzt gespeichert" (inkl. Feiertage + Betriebsferien)
        lastSavedSettingsRef.current = JSON.stringify(finalSettings);
        lastSavedEntriesRef.current = JSON.stringify(finalEntries);

        // In AppContext importieren — IMPORT_DATA adds holidays in the reducer,
        // mergedEntries already includes company vacation
        importData(mergedEntries, finalSettings, currentYear);

        // Urlaubsübertrag aus API in State setzen (expired wird bereits in apiStorage berechnet)
        setCarryOver(carryOver);

        // Settings-Segmente in AppState setzen
        if (settingsSegments.length > 0) {
          setSettingsSegments(settingsSegments);
        }

        hasLoadedFromAPI.current = true;
        console.log('APIDataLoader: Laden abgeschlossen');
      } catch (e) {
        console.error('APIDataLoader: Fehler beim Laden:', e);
        setError(e instanceof Error ? e.message : 'Fehler beim Laden der Daten');
      } finally {
        setIsLoading(false);
      }
    };

    void loadFromAPI();
  }, [isAuthenticated, user, isLoggedIn, getSettings, getEntries, getCarryOver, getCompanyVacationRule, getMySettingsSegments, getMe, state.year, importData, setSettingsSegments, setUserWageType, setCarryOver]);

  // Bei Änderungen: Nach API speichern (debounced 1000ms)
  useEffect(() => {
    if (!hasLoadedFromAPI.current) {
      return;
    }

    // Debounce: reset timer on each change
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      void executeSave();
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [state.settings, state.entries, executeSave]);

  // Vor dem Verlassen der Seite: keepalive fetch
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Cancel debounce timer
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      // Cancel retry timer
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Check if there are unsaved changes
      const currentSettings = JSON.stringify(stateRef.current.settings);
      const currentEntries = JSON.stringify(stateRef.current.entries);

      if (currentSettings !== lastSavedSettingsRef.current || currentEntries !== lastSavedEntriesRef.current) {
        // SAFETY: never push empty entries via beforeunload
        const entriesObj = stateRef.current.entries;
        const entriesCount = Object.keys(entriesObj).length;
        const shouldSaveEntries = currentEntries !== lastSavedEntriesRef.current && entriesCount > 0;

        console.log('APIDataLoader: beforeunload - saving with keepalive fetch');
        const token = localStorage.getItem('jwt_token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

        if (shouldSaveEntries) {
          void fetch(`${apiUrl}/entries/${stateRef.current.year}`, {
            method: 'PUT',
            headers,
            body: currentEntries,
            keepalive: true,
          });
        }
        if (currentSettings !== lastSavedSettingsRef.current) {
          void fetch(`${apiUrl}/settings`, {
            method: 'PUT',
            headers,
            body: currentSettings,
            keepalive: true,
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Urlaubsübertrag speichern
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedFromAPI.current || !state.carryOverVacation) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        console.log('APIDataLoader: Speichere Urlaubsübertrag');
        await saveCarryOver(state.year, state.carryOverVacation!);
      } catch (e) {
        console.error('APIDataLoader: Fehler beim Speichern des Urlaubsübertrags:', e);
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, state.carryOverVacation, state.year, saveCarryOver]);

  // Jahr-Wechsel: Flush old year, then load new year data
  const prevYearRef = useRef(state.year);
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedFromAPI.current) {
      return;
    }

    if (prevYearRef.current === state.year) {
      return;
    }

    const oldYear = prevYearRef.current;

    const loadYearData = async () => {
      console.log('APIDataLoader: Jahr gewechselt von', oldYear, 'zu', state.year);

      // Flush any pending saves for the OLD year before switching
      await flushSave();

      setIsLoading(true);

      try {
        const [entries, yearCarryOver, newSettingsSegments] = await Promise.all([
          getEntries(state.year),
          getCarryOver(state.year),
          getMySettingsSegments(state.year),
        ]);

        // Urlaubsübertrag für neues Jahr setzen (expired wird bereits in apiStorage berechnet)
        setCarryOver(yearCarryOver);

        let mergedEntries: Record<string, DayEntry> = entries || {};

        // Betriebsferien VOR dem Dispatch mergen (gleicher Fix wie in loadFromAPI)
        const rule = companyVacationRuleRef.current;
        if (rule) {
          const vacationDates = getCompanyVacationDates(rule, state.year);
          mergedEntries = mergeCompanyVacationIntoEntries(mergedEntries, vacationDates, userWageTypeRef.current);
        }

        // Feiertage hinzufügen BEVOR wir lastSaved setzen
        const finalEntries = addHolidaysToEntries(mergedEntries, state.year, state.settings.federalState);

        lastSavedEntriesRef.current = JSON.stringify(finalEntries);

        importData(mergedEntries, state.settings, state.year);

        // Settings-Segmente für neues Jahr setzen
        if (newSettingsSegments.length > 0) {
          setSettingsSegments(newSettingsSegments);
        }

        prevYearRef.current = state.year;
      } catch (e) {
        console.error('APIDataLoader: Fehler beim Laden der Jahresdaten:', e);
      } finally {
        setIsLoading(false);
      }
    };

    void loadYearData();
  }, [isAuthenticated, state.year, state.settings, getEntries, getCarryOver, getMySettingsSegments, importData, setSettingsSegments, setCarryOver, flushSave]);

  // Cleanup retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Fehler beim Laden</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <SaveStatusIndicator
        status={saveStatus}
        errorMessage={saveError}
        onRetry={() => executeSave(true)}
      />
    </>
  );
}
